/**
 * INPUT MANAGER — abstracts keyboard, mouse, touch into getMovementDelta().
 */

import { isDevPerfUiTarget } from "./dev-perf-dialog.js";
import { isVolumeControlTarget } from "./embed-scroll-guard.js";

/** Smoothstep ramp from min to max speed over hold duration (ms). */
export function getKeyboardRampSpeed(speedMin, speedMax, rampMs, heldSinceMs, nowMs) {
  var min = Number(speedMin);
  var max = Number(speedMax);
  if (!isFinite(min) || min < 0) min = 0;
  if (!isFinite(max) || max < 0) max = min;
  var ramp = Number(rampMs);
  if (!isFinite(ramp) || ramp <= 0 || min === max) return max;
  var now = nowMs != null ? Number(nowMs) : (typeof performance !== "undefined" ? performance.now() : 0);
  var heldSince = heldSinceMs != null ? Number(heldSinceMs) : now;
  var heldMs = Math.max(0, now - heldSince);
  var t = Math.min(1, heldMs / ramp);
  t = t * t * (3 - 2 * t);
  return min + (max - min) * t;
}

export function InputManager(opts) {
  this.opts = opts || {};
  this.pointerSensitivity = this.opts.pointerSensitivity != null ? this.opts.pointerSensitivity : 1.5;
  this.playerMoveSpeed = this.opts.playerMoveSpeed != null ? this.opts.playerMoveSpeed : 350;
  this.getCssToGameScale = typeof this.opts.getCssToGameScale === "function"
    ? this.opts.getCssToGameScale
    : function () { return 1; };

  this.moveDirection = 0;
  this.pointerVelocity = 0;
  this.lastPointerX = null;
  this.lastTouchX = null;
  /** After keyboard aim, ignore stale cursor until the pointer moves again. */
  this.pointerFollowSuspended = false;
  this.keyboardMoveSpeedMin = this.opts.keyboardMoveSpeedMin != null
    ? this.opts.keyboardMoveSpeedMin
    : this.playerMoveSpeed;
  this.keyboardMoveSpeedMax = this.opts.keyboardMoveSpeedMax != null
    ? this.opts.keyboardMoveSpeedMax
    : this.playerMoveSpeed;
  this.keyboardMoveRampMs = this.opts.keyboardMoveRampMs != null ? this.opts.keyboardMoveRampMs : 0;
  /** performance.now()-style timestamp when the current arrow key was pressed. */
  this.keyboardHeldSinceMs = null;

  this.boundKeyDown = this.handleKeyDown.bind(this);
  this.boundKeyUp = this.handleKeyUp.bind(this);
  this.boundMouseMove = this.handleMouseMove.bind(this);
  this.boundMouseLeave = this.handleMouseLeave.bind(this);
  this.boundMouseEnter = this.handleMouseEnter.bind(this);
  this.boundTouchStart = this.handleTouchStart.bind(this);
  this.boundTouchMove = this.handleTouchMove.bind(this);
  this.boundTouchEnd = this.handleTouchEnd.bind(this);
  this.boundVisibilityChange = this.handleVisibilityChange.bind(this);
}

InputManager.prototype.attach = function () {
  document.addEventListener("keydown", this.boundKeyDown);
  document.addEventListener("keyup", this.boundKeyUp);
  document.addEventListener("mousemove", this.boundMouseMove);
  document.addEventListener("mouseleave", this.boundMouseLeave);
  document.addEventListener("mouseenter", this.boundMouseEnter);
  document.addEventListener("touchstart", this.boundTouchStart, { passive: true });
  document.addEventListener("touchmove", this.boundTouchMove, { passive: false });
  document.addEventListener("touchend", this.boundTouchEnd);
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", this.boundVisibilityChange);
  }
};

InputManager.prototype.detach = function () {
  document.removeEventListener("keydown", this.boundKeyDown);
  document.removeEventListener("keyup", this.boundKeyUp);
  document.removeEventListener("mousemove", this.boundMouseMove);
  document.removeEventListener("mouseleave", this.boundMouseLeave);
  document.removeEventListener("mouseenter", this.boundMouseEnter);
  document.removeEventListener("touchstart", this.boundTouchStart);
  document.removeEventListener("touchmove", this.boundTouchMove);
  document.removeEventListener("touchend", this.boundTouchEnd);
  if (typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", this.boundVisibilityChange);
  }
};

InputManager.prototype._notifyGameplayInput = function () {
  if (typeof this.opts.onGameplayInput === "function") {
    this.opts.onGameplayInput();
  }
};

InputManager.prototype.handleKeyDown = function (e) {
  var newDir = 0;
  if (e.key === "ArrowLeft" || e.code === "ArrowLeft") newDir = -1;
  else if (e.key === "ArrowRight" || e.code === "ArrowRight") newDir = 1;
  if (!newDir) return;
  this._notifyGameplayInput();
  if (typeof this.opts.onArrowKey === "function") {
    this.opts.onArrowKey(newDir < 0 ? "left" : "right");
  }
  if (newDir !== this.moveDirection) {
    var ts = e.timeStamp;
    this.keyboardHeldSinceMs = ts != null && ts > 0
      ? ts
      : (typeof performance !== "undefined" ? performance.now() : 0);
  }
  this.moveDirection = newDir;
};

InputManager.prototype.handleKeyUp = function (e) {
  if ((e.key === "ArrowLeft" || e.code === "ArrowLeft") && this.moveDirection === -1) {
    this.moveDirection = 0;
    this.keyboardHeldSinceMs = null;
    this.pointerFollowSuspended = true;
  } else if ((e.key === "ArrowRight" || e.code === "ArrowRight") && this.moveDirection === 1) {
    this.moveDirection = 0;
    this.keyboardHeldSinceMs = null;
    this.pointerFollowSuspended = true;
  }
};

InputManager.prototype.handleVisibilityChange = function () {
  if (typeof document !== "undefined" && document.hidden) {
    this.clearKeyboardState();
  }
};

InputManager.prototype.hasKeyboardDirection = function () {
  return this.moveDirection !== 0;
};

InputManager.prototype.clearKeyboardState = function () {
  this.moveDirection = 0;
  this.keyboardHeldSinceMs = null;
  this.pointerFollowSuspended = true;
};

InputManager.prototype.handleMouseMove = function (e) {
  if (this.lastPointerX !== null) {
    this.pointerVelocity = (e.clientX - this.lastPointerX) * this.pointerSensitivity * this.getCssToGameScale();
  }
  this.lastPointerX = e.clientX;
  this.pointerFollowSuspended = false;
  this._notifyGameplayInput();
};

InputManager.prototype.handleMouseLeave = function () {
  this.lastPointerX = null;
  this.pointerVelocity = 0;
};

InputManager.prototype.handleMouseEnter = function (e) {
  this.lastPointerX = e.clientX;
};

/**
 * Seed absolute pointer follow from a known clientX (e.g. last cursor before stage start).
 * @param {number} clientX
 */
InputManager.prototype.seedPointerClientX = function (clientX) {
  var x = Number(clientX);
  if (!isFinite(x)) return;
  this.lastPointerX = x;
  this.pointerFollowSuspended = false;
};

InputManager.prototype.handleTouchStart = function (e) {
  if (isDevPerfUiTarget(e.target)) return;
  if (isVolumeControlTarget(e.target)) return;
  if (e.touches.length > 0) {
    this.lastTouchX = e.touches[0].clientX;
    this.pointerFollowSuspended = false;
    this._notifyGameplayInput();
  }
};

InputManager.prototype.handleTouchMove = function (e) {
  if (!e.touches.length) return;
  if (isDevPerfUiTarget(e.target)) return;
  if (isVolumeControlTarget(e.target)) return;
  if (e.cancelable) e.preventDefault();
  var touch = e.touches[0];
  if (this.lastTouchX !== null) {
    this.pointerVelocity = (touch.clientX - this.lastTouchX) * this.pointerSensitivity * this.getCssToGameScale();
  }
  this.lastTouchX = touch.clientX;
  this.pointerFollowSuspended = false;
};

InputManager.prototype.handleTouchEnd = function () {
  this.lastTouchX = null;
  this.pointerVelocity = 0;
  // Suspend stale mouse fallback until the next touch or mouse move.
  this.pointerFollowSuspended = true;
};

InputManager.prototype._getKeyboardMoveSpeed = function (currentTime) {
  if (this.moveDirection === 0) return 0;
  if (!this.keyboardMoveRampMs || this.keyboardMoveSpeedMin === this.keyboardMoveSpeedMax) {
    return this.playerMoveSpeed;
  }
  var now = currentTime != null
    ? currentTime
    : (typeof performance !== "undefined" ? performance.now() : 0);
  if (this.keyboardHeldSinceMs == null) this.keyboardHeldSinceMs = now;
  return getKeyboardRampSpeed(
    this.keyboardMoveSpeedMin,
    this.keyboardMoveSpeedMax,
    this.keyboardMoveRampMs,
    this.keyboardHeldSinceMs,
    now
  );
};

InputManager.prototype.getMovementDelta = function (deltaTime, active, currentTime) {
  if (active === false) return 0;
  var keyboardSpeed = this._getKeyboardMoveSpeed(currentTime);
  var amount = keyboardSpeed * deltaTime * this.moveDirection + this.pointerVelocity;
  this.pointerVelocity = 0;
  return amount;
};

/**
 * Absolute pointer position for systems that want "follow cursor" behavior.
 * Returns the latest clientX from touch (if active) or mouse, or null when inactive.
 */
InputManager.prototype.getPointerClientX = function () {
  if (this.lastTouchX != null) return this.lastTouchX;
  return this.lastPointerX;
};

/**
 * Pointer X for absolute "follow cursor" — null while follow is suspended after keyboard use.
 */
InputManager.prototype.getFollowPointerClientX = function () {
  if (this.pointerFollowSuspended) return null;
  return this.getPointerClientX();
};
